<template>
  <div>
    <div
      class="Content"
      :class="{ 'is-loading': ui.isLoading }"
    >
      <div class="Content-group">
        <div :class="['Section-EventRecords']">
          <div
            v-if="record"
            class="EventResponse"
          >
            <div class="EventResponse-wrapper">
              <h2>Record</h2>
              <div
                v-for="answer in record.questions"
                :key="answer.id"
                class="EventResponse-question-answer"
              >
                <h3>
                  {{ answer.label }} <span
                    v-if="answer.isFileUploadQuestion"
                    class="Icon Icon-cloud-download"
                  />
                </h3>
                <template v-if="Array.isArray(answer.answer)">
                  <span
                    v-for="option in answer.answer"
                    :key="option"
                    class="Tag"
                  >{{ option }}</span>
                </template>
                <template v-else-if="answer.isFileUploadQuestion">
                  <div v-if="answer.fileMetadata.uuid !== undefined">
                    <ul class="EventResponse-question-answer-file-upload">
                      <li>UUID: {{ answer.fileMetadata.uuid }}</li>
                      <li>Original name: {{ answer.fileMetadata.originalName }}</li>
                      <li>Extension: {{ answer.fileMetadata.extension }}</li>
                      <li>Type: {{ answer.fileMetadata.mimeType }}</li>
                      <li>Size: {{ answer.fileMetadata.size }}</li>
                      <li v-if="answer.fileMetadata.isCleaned || answer.fileMetadata.isInfected">
                        Scanned At: {{ answer.fileMetadata.scannedAt }}
                      </li>
                    </ul>
                    <div v-if="answer.fileMetadata.isCleaned">
                      <a
                        :href="answer.fileMetadata.downloadUrl"
                        title="Click to download the file"
                        download
                      >Download <span class="Icon Icon-file" />
                      </a>
                    </div>
                    <div v-else-if="answer.fileMetadata.isInfected">
                      <span class="Icon Icon-exclamation-triangle" /> Our anti virus scan found a threat in the file, so
                      we’ve automatically deleted it for your protection.
                    </div>
                    <div v-else-if="answer.fileMetadata.isWaitingToBeScanned">
                      <span class="Icon Icon-clock" /> In queue to be scanned by our anti virus, please check again
                      later.
                    </div>
                    <div v-else-if="answer.fileMetadata.isSpoofed">
                      <span class="Icon Icon-exclamation-triangle" /> The stored file does not match the one uploaded by
                      the student.
                    </div>
                    <div v-else-if="answer.fileMetadata.isNotFound">
                      <span class="Icon Icon-exclamation-triangle" /> The file was not found
                    </div>
                    <div v-else-if="answer.fileMetadata.isFailedToScan">
                      <span class="Icon Icon-exclamation-triangle" /> The file couldn't be scanned
                    </div>
                  </div>
                  <div v-else>
                    Did not answer
                  </div>
                </template>
                <template v-else>
                  <div :class="{ QuestionIsLongText: answer.type ==='long_text'}">
                    {{ answer.answer }}
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div
            v-if="record"
            class="EventDetails"
          >
            <h3>Registration Date</h3>
            <p>{{ record.registrationDate }}</p>
            <h3>Times Registered</h3>
            <p>{{ record.timesRegistered }}</p>
            <template v-if="record.automations.on_registration_email">
              <h3>Automation / On Registration</h3>
              <div
                class="Automation"
              >
                <span
                  class="Icon-s Icon-envelope"
                  :title="titleForAutomationEnvelopeIcon(record.automations.on_registration_email.success)"
                  :class="{ AutomationFailed: !record.automations.on_registration_email.success}"
                />
                <div class="AutomationDetails">
                  <p class="AutomationName">
                    {{ record.automations.on_registration_email.automationName }}
                  </p>
                  <p class="AutomationDate">
                    {{ record.automations.on_registration_email.sentAt }}
                  </p>
                </div>
              </div>
            </template>
            <template v-if="record.automations.when_form_ends">
              <h3>Automation / When Event Ends</h3>
              <div
                class="Automation"
              >
                <span
                  class="Icon-s Icon-envelope"
                  :title="titleForAutomationEnvelopeIcon(record.automations.when_form_ends.success)"
                  :class="{ AutomationFailed: !record.automations.when_form_ends.success}"
                />
                <div class="AutomationDetails">
                  <p class="AutomationName">
                    {{ record.automations.when_form_ends.automationName }}
                  </p>
                  <p class="AutomationDate">
                    {{ record.automations.when_form_ends.sentAt }}
                  </p>
                </div>
              </div>
            </template>
            <template v-if="record.automations.scheduled.length > 0">
              <h3>Automation / Scheduled</h3>
              <div
                v-for="(scheduledAutomation, tIndex) in record.automations.scheduled"
                :key="tIndex"
                class="Automation"
              >
                <span
                  class="Icon-s Icon-envelope"
                  :title="titleForAutomationEnvelopeIcon(scheduledAutomation.success)"
                  :class="{ AutomationFailed: !scheduledAutomation.success}"
                />
                <div class="AutomationDetails">
                  <p class="AutomationName">
                    {{ (tIndex + 1) }}) {{ scheduledAutomation.automationName }}
                  </p>
                  <p class="AutomationDate">
                    {{ scheduledAutomation.sentAt }}
                  </p>
                </div>
              </div>
            </template>
            <div v-if="record.eventHasQuestionsWithScores">
              <h3>
                Score <span
                  v-if="hasScoreBreakDown"
                  title="Click to see score breakdown."
                  style="cursor:pointer;"
                  class="Icon-s Icon-list-alt"
                  @click="ui.showResponseScoreBreakdownModal = true"
                />
              </h3>
              <p>{{ record.score.score }}</p>
              <response-score-breakdown-modal
                v-model="ui.showResponseScoreBreakdownModal"
                :score-breakdown="record.score.breakdown"
              />
            </div>
            <div v-if="formHasQuestionsWithFilesToDownload">
              <h3>About File Upload Questions</h3>
              <p>
                Although the files are scanned by our antivirus, it is important to remember that no virus scanner is
                100% effective, and though rare, some viruses may still get through.
              </p>
              <p>
                It’s strongly advised to keep your local antivirus up to date and scan the files yourself before
                opening, especially if the time the file was scanned is already some days old.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div>
        <router-link
          v-if="record"
          :to="{ name: 'event-records', params: { eventId: event.id }}"
          class="BackButton Button Button--green-outline u-floatLeft"
        >
          Back
        </router-link>
        <a
          v-if="record && record.studentProfileUrl !== null"
          :href="record.studentProfileUrl"
          class="ViewProfileButton Button Button--green u-floatRight"
        >View Profile</a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ResponseScoreBreakdownModal from '../modals/response-score-breakdown.vue';

export default {
  components: {
    ResponseScoreBreakdownModal,
  },
  data() {
    return {
      ui: {
        showResponseScoreBreakdownModal: false,
        isLoading: false,
      },
      record: null,
    };
  },
  computed: {
    ...mapState('events', {
      event: (state) => state.event,
    }),
    formHasQuestionsWithFilesToDownload() {
      return this.event.questions.filter((question) => {
        return question.has_files;
      }).length > 0;
    },
    hasScoreBreakDown() {
      return this.record.score.breakdown !== undefined && Object.keys(this.record.score.breakdown).length > 0;
    },
  },
  mounted() {
    const {
      eventId,
      recordId,
    } = this.$route.params;
    if (
      eventId !== 'undefined'
        && recordId !== 'undefined'
    ) {
      this.ui.isLoading = true;

      this.getSingleEventRecord({
        eventId,
        recordId,
      })
        .then((data) => {
          this.ui.isLoading = false;
          this.record = data.data;
        })
        .catch(({ data }) => {
          this.ui.isLoading = false;
          this.$bus.$emit('displayMsg', {
            text: data.data.message,
            type: 'warning',
          });
        });
    }
  },
  methods: {
    ...mapActions('events', ['getEvent', 'getSingleEventRecord']),
    titleForAutomationEnvelopeIcon(wasEmailAutomationDeliveredWithSuccess) {
      return wasEmailAutomationDeliveredWithSuccess ? 'Delivered with success' : 'Failed to deliver';
    },
  },
};
</script>

<style lang="less" scoped>

.BackButton {
  margin-top: 10px;
}

.ViewProfileButton {
  margin-top: 10px;
}

.Section-EventRecords {
  display: flex;
}

.EventResponse {
  border-right: 1px solid lightgray;
  flex-basis: 70%;

  .EventResponse-wrapper {
    margin: 15px;
    background: #d8d8d842;
    padding: 25px;
  }

  .EventResponse-question-answer {
    padding: 10px;
  }

  .EventResponse-question-answer-file-upload {
    li {
      margin-bottom: 3px;
    }
  }

  .QuestionIsLongText {
    white-space: pre-line;
  }

  h3 {
    font-weight: bold;
    margin-bottom: 5px;
  }

  .Tag {
    border-radius: 3px;
    background: #e0e4ea;
    color: #424242;
    height: 22px;
    width: auto;
    line-height: 22px;
    padding: 0 10px;
    font-size: 0.9rem;
    margin: 5px 5px 1em 0;
  }
}

.EventDetails {
  flex-basis: 30%;
  padding: 25px;

  h3 {
    font-weight: bold;
    margin-bottom: 5px;
  }

  .Automation {
    display: flex;
    border: 1px solid lightgray;
    border-radius: 8px;
    padding: 10px;
    margin-bottom: 10px;

    .Icon-s {
      margin-right: 10px;
      font-size: 1.2rem;
    }

    .AutomationFailed {
      color: red;
    }

    .AutomationDetails {

      .AutomationName {
        font-weight: bold;
      }

      p {
        margin: 0;
      }
    }
  }
}
</style>
